/**
 * Base class for ambient occlusion passes (SAOPass2 and GTAOPass)
 * Copyright Higharc Inc.
 */
import * as THREE from "three";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { materialMultiplyByRed, materialShowAONormals } from "./aoDebugVisualizationMaterials";
import { DepthLimitedBlur } from "./DepthLimitedBlur";
import { DepthLimitedPoissonBlur } from "./DepthLimitedPoissonBlur";
import type { DepthNormalPass } from "./DepthNormalPass";
import { SSFXRendererOutput } from "./SSFXRendererOutput";

// It doesn't necessarily make sense to use every mip level in the shader. This stride value controls how many depth mip levels are picked for the shader.
export const DEPTH_MIP_STRIDE = 2;

export interface AOPassBlurParams {
  ssaoBlur: boolean;
  output: SSFXRendererOutput;

  // Alternative blur based on Poisson disk sampling.
  usePoissonBlur: boolean;

  // blurKernelSamples is the only parameter here that affects performance.
  // The number of samples and stdDev can be tuned to balance between noise and accuracy.
  blurKernelSamples: number;
  blurStdDev: number;

  // Good values for these are affected mostly by numerical precision - not really useful for artistic tuning.
  blurDepthCutoff: number;
  blurNormalAngleCutoff: number;

  // Suggested to be left enabled for better quality.
  useNormalTextureInBlur: boolean;
}

export class ClearSettingsSaver {
  private clearSettingsSaved: boolean = false;
  private oldAutoClear: boolean = false;
  private oldClearColor: THREE.Color = new THREE.Color();
  private oldClearAlpha: number = 1.0;

  saveClearSettings(renderer: THREE.WebGLRenderer) {
    if (this.clearSettingsSaved) {
      throw "Trying to save clear settings when they're already saved - make sure save and restore are paired.";
    }
    this.oldAutoClear = renderer.autoClear;
    renderer.getClearColor(this.oldClearColor);
    this.oldClearAlpha = renderer.getClearAlpha();
    this.clearSettingsSaved = true;
  }

  restoreClearSettings(renderer: THREE.WebGLRenderer) {
    if (!this.clearSettingsSaved) {
      throw "Trying to restore clear settings when they're already restored - make sure save and restore are paired.";
    }
    renderer.autoClear = this.oldAutoClear;
    renderer.setClearColor(this.oldClearColor, this.oldClearAlpha);
    this.clearSettingsSaved = false;
  }
}

export abstract class AOPass extends EffectComposerPass {
  camera: THREE.Camera;
  randomSeed: number = 0.5;

  protected unpaddedSize: THREE.Vector2;
  protected paddedSize: THREE.Vector2;
  protected aoResolutionMultiplier: number;
  protected aoRenderResolution: THREE.Vector2;

  protected perspectiveBlur: DepthLimitedBlur;
  protected orthographicBlur: DepthLimitedBlur;
  protected perspectiveBlurNormalMap: DepthLimitedBlur;
  protected orthographicBlurNormalMap: DepthLimitedBlur;
  protected perspectiveBlurPoisson: DepthLimitedPoissonBlur;
  protected orthographicBlurPoisson: DepthLimitedPoissonBlur;

  protected aoRenderTarget: THREE.WebGLRenderTarget;
  protected aoDebugRenderTarget: THREE.WebGLRenderTarget | undefined;
  protected blurIntermediateRenderTarget: THREE.WebGLRenderTarget | undefined;
  protected blurFinalRenderTarget: THREE.WebGLRenderTarget;

  protected fsQuad: FullScreenQuad;
  protected materialShowNormals: THREE.ShaderMaterial;
  protected materialMultiplyByRed: THREE.ShaderMaterial;

  protected readonly depthNormalPass: DepthNormalPass;

  protected readonly clearSettingsSaver: ClearSettingsSaver = new ClearSettingsSaver();

  constructor(
    camera: THREE.Camera,
    depthNormalPass: DepthNormalPass,
    aoResolutionMultiplier: number,
    size?: THREE.Vector2,
  ) {
    super();
    this.aoResolutionMultiplier = aoResolutionMultiplier;
    this.camera = camera;
    this.depthNormalPass = depthNormalPass;

    this.unpaddedSize = size !== undefined ? new THREE.Vector2(size.x, size.y) : new THREE.Vector2(256, 256);
    this.paddedSize = new THREE.Vector2(this.unpaddedSize.x, this.unpaddedSize.y);
    this.aoRenderResolution = new THREE.Vector2(
      Math.ceil(this.paddedSize.x * aoResolutionMultiplier),
      Math.ceil(this.paddedSize.y * aoResolutionMultiplier),
    );

    // Cache different blur shaders for use with different cameras and dynamically changed parameters
    this.perspectiveBlur = new DepthLimitedBlur();
    this.orthographicBlur = new DepthLimitedBlur();
    this.perspectiveBlurNormalMap = new DepthLimitedBlur();
    this.orthographicBlurNormalMap = new DepthLimitedBlur();
    this.perspectiveBlurPoisson = new DepthLimitedPoissonBlur();
    this.orthographicBlurPoisson = new DepthLimitedPoissonBlur();

    this.perspectiveBlurNormalMap.params.useNormalTexture = true;
    this.orthographicBlurNormalMap.params.useNormalTexture = true;
    this.perspectiveBlurPoisson.params.useNormalTexture = true;
    this.orthographicBlurPoisson.params.useNormalTexture = true;

    this.materialShowNormals = materialShowAONormals();
    this.materialMultiplyByRed = materialMultiplyByRed();

    this.aoRenderTarget = new THREE.WebGLRenderTarget(this.aoRenderResolution.x, this.aoRenderResolution.y, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RedFormat,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.blurFinalRenderTarget = this.aoRenderTarget.clone();

    this.fsQuad = new FullScreenQuad();
  }

  protected renderPass(
    renderer: THREE.WebGLRenderer,
    passMaterial: THREE.Material,
    renderTarget: THREE.WebGLRenderTarget | null,
    clearColor?: THREE.ColorRepresentation,
    clearAlpha?: number,
  ) {
    renderer.setRenderTarget(renderTarget);

    const clearNeeded = clearColor !== undefined && clearColor !== null;
    if (clearNeeded) {
      renderer.setClearColor(clearColor);
      renderer.setClearAlpha(clearAlpha || 0.0);
      renderer.clear();
    }

    this.fsQuad.material = passMaterial;
    this.fsQuad.render(renderer);
  }

  protected getAoRenderTarget(output: SSFXRendererOutput): THREE.WebGLRenderTarget {
    if (output === SSFXRendererOutput.NORMAL_FOR_AO) {
      if (!this.aoDebugRenderTarget) {
        this.aoDebugRenderTarget = new THREE.WebGLRenderTarget(this.aoRenderResolution.x, this.aoRenderResolution.y, {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          format: THREE.RGBAFormat,
          depthBuffer: false,
          stencilBuffer: false,
        });
      }
      return this.aoDebugRenderTarget;
    }
    return this.aoRenderTarget;
  }

  protected blurAO(renderer: THREE.WebGLRenderer, blurParams: AOPassBlurParams) {
    if (!blurParams.ssaoBlur) {
      return;
    }

    // Blurring SAO texture, unless our output is normals.
    const aoOutputIsNormals = blurParams.output === SSFXRendererOutput.NORMAL_FOR_AO;
    if (aoOutputIsNormals) {
      return;
    }

    const isPerspectiveCamera = (this.camera as THREE.PerspectiveCamera).isPerspectiveCamera;

    const blurColorMask: [boolean, boolean, boolean, boolean] = [true, false, false, false];

    if (blurParams.usePoissonBlur) {
      const blur = isPerspectiveCamera ? this.perspectiveBlurPoisson : this.orthographicBlurPoisson;

      blur.params.kernelSamples = Math.floor(blurParams.blurKernelSamples);

      blur.blur(
        renderer,
        this.camera,
        this.aoRenderTarget.texture,
        this.blurFinalRenderTarget,
        this.depthNormalPass,
        blurColorMask,
      );
    } else {
      let blur: DepthLimitedBlur | undefined;
      if (blurParams.useNormalTextureInBlur) {
        blur = isPerspectiveCamera ? this.perspectiveBlurNormalMap : this.orthographicBlurNormalMap;
      } else {
        blur = isPerspectiveCamera ? this.perspectiveBlur : this.orthographicBlur;
      }

      blur.params.kernelSamples = Math.floor(blurParams.blurKernelSamples);
      blur.params.stdDev = blurParams.blurStdDev;
      blur.params.depthCutoff = blurParams.blurDepthCutoff;
      blur.params.normalAngleCutoff = blurParams.blurNormalAngleCutoff;

      if (!this.blurIntermediateRenderTarget) {
        this.blurIntermediateRenderTarget = this.aoRenderTarget.clone();
      }

      blur.blur(
        renderer,
        this.camera,
        this.aoRenderTarget.texture,
        this.blurIntermediateRenderTarget,
        this.blurFinalRenderTarget,
        this.depthNormalPass,
        blurColorMask,
      );
    }
  }

  protected renderOutput(
    renderer: THREE.WebGLRenderer,
    output: SSFXRendererOutput,
    writeBuffer: THREE.WebGLRenderTarget | null,
  ) {
    if (output === SSFXRendererOutput.AO) {
      const outputClearColor = 0xffffff;
      const outputClearAlpha = 1.0;
      this.materialMultiplyByRed.uniforms["map"].value = this.screenSpaceAoMap;
      this.renderPass(renderer, this.materialMultiplyByRed, writeBuffer, outputClearColor, outputClearAlpha);
      return;
    }
    if (output === SSFXRendererOutput.NORMAL_FOR_AO) {
      this.materialShowNormals.uniforms["map"].value = this.aoDebugRenderTarget?.texture ?? null;
      this.renderPass(renderer, this.materialShowNormals, writeBuffer);
      return;
    }
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    this.unpaddedSize = new THREE.Vector2(width, height);
    this.refreshResolution();
  }

  protected refreshResolution() {
    this.paddedSize = this.depthNormalPass.paddedSize;
    this.aoRenderResolution = new THREE.Vector2(
      Math.ceil(this.paddedSize.x * this.aoResolutionMultiplier),
      Math.ceil(this.paddedSize.y * this.aoResolutionMultiplier),
    );

    this.aoRenderTarget.setSize(this.aoRenderResolution.x, this.aoRenderResolution.y);
    this.aoDebugRenderTarget?.setSize(this.aoRenderResolution.x, this.aoRenderResolution.y);
    this.blurIntermediateRenderTarget?.setSize(this.paddedSize.x, this.paddedSize.y);
    this.blurFinalRenderTarget.setSize(this.paddedSize.x, this.paddedSize.y);
  }

  dispose() {
    this.aoRenderTarget.dispose();
    this.aoDebugRenderTarget?.dispose();
    this.blurIntermediateRenderTarget?.dispose();
    this.blurFinalRenderTarget.dispose();

    this.perspectiveBlur.dispose();
    this.orthographicBlur.dispose();
    this.perspectiveBlurNormalMap.dispose();
    this.orthographicBlurNormalMap.dispose();
    this.perspectiveBlurPoisson.dispose();
    this.orthographicBlurPoisson.dispose();

    this.materialShowNormals.dispose();
    this.materialMultiplyByRed.dispose();
  }

  protected abstract get screenSpaceAoMap(): THREE.Texture;
}
